import { Component, computed, inject, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import * as fromGenerated from '../../_generated';
import * as fromAssistantIcon from './assistants.icon.helper';

type MoreOptionsDropdownMenuOption = fromSeriousSystem.DropdownMenuOption & {
  type:
    | 'start-chat'
    | 'add-to-favorites'
    | 'remove-from-favorites'
    | 'duplicate'
    | 'edit'
    | 'delete';
  action: () => void;
  sortOrder: number;
  disabled: boolean;
  error: boolean;
};

@Component({
  selector: 'squadbox-assistant-card',
  imports: [
    fromSeriousSystem.IconButtonDirective,
    fromSeriousSystem.UseAssistantCustomIconDirective,
    fromSeriousSystem.UseAssistantIconDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
    fromSeriousSystem.DropdownMenuComponent,
    fromSeriousSystem.DropdownMenuTriggerDirective,
    fromSeriousSystem.SpinnerDirective,
    TranslateModule,
    MatTooltipModule,
  ],
  template: `
    <div
      role="button"
      [attr.tabindex]="status() === 'ready' ? 0 : -1"
      [attr.aria-disabled]="status() === 'ready' ? null : true"
      [class.grid-template-area]="'logo'"
      class="
        relative
        flex items-center justify-center
        size-12
        text-primary-500/50
      "
      [class.cursor-pointer]="status() === 'ready'"
      (click)="handleClickRowCTA()"
      (keydown.enter)="handleClickRowCTA()"
      (keydown.space)="handleClickRowCTA(); $event.preventDefault()"
    >
      <!-- Status badge -->
      @if (status() === 'processing' || status() === 'failed') {
        <div
          class="
            absolute top-0 right-0 transform -translate-x-[5%] translate-y-[5%]
            flex items-center justify-center
            size-6 p-1 rounded-full border-2 border-shades-white
          "
          [class.bg-primary-500]="status() === 'processing'"
          [class.bg-error-500]="status() === 'failed'"
        >
          @if (status() === 'processing') {
            <div sdSpinner color="white" size="xs" class="h-full"></div>
          } @else if (status() === 'failed') {
            <svg
              sdUseUIBasicIcon="warning-filled"
              class="size-3 text-shades-white"
            ></svg>
          }
        </div>
      }
      @switch (fromAssistantIcon.getLogoType(logo())) {
        @case ('assistant') {
          <svg
            [sdUseAssistantIcon]="fromAssistantIcon.getAssistantIcon(logo())"
            class="size-6"
          ></svg>
        }
        @case ('custom') {
          <svg
            [sdUseAssistantCustomIcon]="
              fromAssistantIcon.getAssistantCustomIcon(logo())
            "
            class="size-6"
          ></svg>
        }
        @default {
          <img [src]="logo()" alt="Assistant Logo" class="size-6" />
        }
      }
    </div>
    <div
      role="button"
      [attr.tabindex]="status() === 'ready' ? 0 : -1"
      [attr.aria-disabled]="status() === 'ready' ? null : true"
      [class.grid-template-area]="'text'"
      class="flex flex-col grow gap-1 overflow-hidden"
      [class.cursor-pointer]="status() === 'ready'"
      (click)="handleClickRowCTA()"
      (keydown.enter)="handleClickRowCTA()"
      (keydown.space)="handleClickRowCTA(); $event.preventDefault()"
    >
      <div
        class="
          flex flex-col tablet-landscape:flex-row tablet-landscape:items-center
          gap-1 tablet-landscape:gap-2
          font-semibold text-neutral-700
        "
      >
        <span class="typo-p3 overflow-hidden text-ellipsis">{{ name() }}</span>
      </div>
      <div
        class="typo-caption line-clamp-2"
        [class.text-neutral-500]="status() !== 'failed'"
        [class.text-error-600]="status() === 'failed'"
      >
        @if (status() === 'processing') {
          {{ 'ASSISTANTS.CARD.PROCESSING_STATUS_DESCRIPTION' | translate }}
        } @else if (status() === 'failed') {
          {{ 'ASSISTANTS.CARD.FAILED_STATUS_DESCRIPTION' | translate }}
        } @else {
          {{ description() }}
        }
      </div>
    </div>
    <div
      [class.grid-template-area]="'action'"
      class="pl-0 tablet-landscape:pl-10"
    >
      <div class="flex items-center gap-2">
        @if (status() !== 'failed' && !isMobile()) {
          @if (isAssistantOwnedByAuthenticatedUser()) {
            <!-- Edit skill (owners only) — replaces the start-new-conversation
                 icon; the row click now starts a conversation (SB-3325 PR-6). -->
            <button
              [matTooltip]="'ASSISTANTS.DROPDOWN_MENU.EDIT' | translate"
              matTooltipPosition="above"
              [matTooltipDisabled]="status() !== 'ready'"
              sdIconButton
              color="neutral"
              variant="icon"
              size="xs"
              [disabled]="status() === 'processing'"
              (click)="editClicked.emit()"
            >
              <svg sdUseUIBasicIcon="edit" class="text-neutral-400"></svg>
            </button>
          }
          @if (!isAdded() && !isMobile()) {
            <!-- Add to favorites -->
            <button
              [matTooltip]="'ASSISTANTS.CARD.ADD_ASSISTANT' | translate"
              matTooltipPosition="above"
              [matTooltipDisabled]="status() !== 'ready'"
              sdIconButton
              color="neutral"
              variant="icon"
              size="xs"
              [disabled]="status() === 'processing'"
              (click)="addClicked.emit()"
            >
              <svg sdUseUIBasicIcon="star" class="text-neutral-400"></svg>
            </button>
          } @else {
            <!-- Remove from favorites -->
            <button
              [matTooltip]="'ASSISTANTS.CARD.REMOVE_ASSISTANT' | translate"
              matTooltipPosition="above"
              [matTooltipDisabled]="status() !== 'ready'"
              sdIconButton
              color="primary"
              variant="icon"
              size="xs"
              [disabled]="status() === 'processing'"
              (click)="removeClicked.emit()"
            >
              <svg sdUseUIBasicIcon="star-filled" class="opacity-50"></svg>
            </button>
          }
        }
        <!-- More options — shown on mobile (holds start-chat/favorite too) and,
             on desktop, only for skills the current user owns (SB-3325 PR-6).
             The trigger and its menu share one @if so the #dropdown template
             ref stays in scope; the menu host is positioned out of the flex
             flow so its mobile drawer does not add an extra gap. -->
        @if (isMobile() || isAssistantOwnedByAuthenticatedUser()) {
          <button
            sdIconButton
            variant="icon"
            color="neutral"
            size="xs"
            [sdDropdownMenuTrigger]="dropdown"
          >
            <svg sdUseUIBasicIcon="more-horizontal"></svg>
          </button>
          <sd-dropdown-menu
            #dropdown
            class="absolute"
            [options]="dropdownMenuOptions()"
          ></sd-dropdown-menu>
        }
      </div>
    </div>
  `,
  styles: [
    `
      @reference '../../../styles.css';

      :host {
        @apply grid grid-cols-[min-content_auto_min-content] gap-2.5 tablet-portrait:gap-4 items-center;
        grid-template-areas: 'logo text action';
      }
    `,
  ],
})
export class AssistantCardComponent {
  private readonly translateService = inject(TranslateService);

  public readonly logo = input.required<string>();
  public readonly name = input.required<string>();
  public readonly description = input.required<string>();
  public readonly userId = input.required<string>();
  public readonly ownedBy = input.required<string>();
  public readonly status =
    input.required<fromGenerated.AssistantView.StatusEnum>();
  public readonly isAdded = input(false);
  public readonly hasNonDeletedDocuments = input.required<boolean>();
  public readonly isMobile = input.required<boolean>();

  public readonly editClicked = output();
  public readonly deleteClicked = output();
  public readonly newConvoClicked = output();
  public readonly addClicked = output();
  public readonly removeClicked = output();
  public readonly duplicateClicked = output();
  protected readonly fromAssistantIcon = fromAssistantIcon;

  private get availableOptions(): Record<
    string,
    MoreOptionsDropdownMenuOption
  > {
    return {
      START_CHAT: {
        type: 'start-chat',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.START_CHAT',
        ) as string,
        icon: { type: 'navigation', icon: 'new-chat' },
        action: () => this.newConvoClicked.emit(),
        sortOrder: 1,
        disabled: false,
        error: false,
      },
      EDIT: {
        type: 'edit',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.EDIT',
        ) as string,
        icon: { type: 'uiBasic', icon: 'edit' },
        action: () => this.editClicked.emit(),
        sortOrder: 2,
        disabled: false,
        error: false,
      },
      ADD_TO_FAVORITES: {
        type: 'add-to-favorites',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.ADD_TO_FAVORITES',
        ) as string,
        icon: { type: 'uiBasic', icon: 'star' },
        action: () => this.addClicked.emit(),
        sortOrder: 4,
        disabled: false,
        error: false,
      },
      REMOVE_FROM_FAVORITES: {
        type: 'remove-from-favorites',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.REMOVE_FROM_FAVORITES',
        ) as string,
        icon: { type: 'uiBasic', icon: 'star' },
        action: () => this.removeClicked.emit(),
        sortOrder: 5,
        disabled: false,
        error: false,
      },
      DUPLICATE: {
        type: 'duplicate',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.DUPLICATE',
        ) as string,
        icon: { type: 'uiBasic', icon: 'copy' },
        action: () => this.duplicateClicked.emit(),
        sortOrder: 6,
        disabled: false,
        error: false,
      },
      DELETE: {
        type: 'delete',
        label: this.translateService.instant(
          'ASSISTANTS.DROPDOWN_MENU.DELETE',
        ) as string,
        icon: { type: 'uiBasic', icon: 'delete' },
        action: () => this.deleteClicked.emit(),
        sortOrder: 7,
        disabled: false,
        error: true,
      },
    };
  }

  protected readonly isAssistantOwnedByAuthenticatedUser = computed(
    () => this.ownedBy() === this.userId(),
  );

  public readonly dropdownMenuOptions = computed(() => {
    const options: MoreOptionsDropdownMenuOption[] = [];
    const available = this.availableOptions;

    // Owner-only management actions (SB-3325 PR-6): non-owners no longer get a
    // desktop menu at all, and on mobile keep only start-chat + favorite below.
    if (this.isAssistantOwnedByAuthenticatedUser()) {
      // Desktop owners edit through the adjacent pencil; keep EDIT in the
      // mobile menu because the external action buttons are hidden there.
      if (this.isMobile()) {
        options.push(available['EDIT']);
      }
      if (!this.hasNonDeletedDocuments()) {
        options.push(available['DUPLICATE']);
      }
      options.push(available['DELETE']);
    }

    // On mobile the start-chat and add/remove-from-favorites actions live
    // inside the dropdown because the external icon buttons are hidden.
    if (this.isMobile()) {
      options.push(
        available['START_CHAT'],
        this.isAdded()
          ? available['REMOVE_FROM_FAVORITES']
          : available['ADD_TO_FAVORITES'],
      );
    }

    return options.sort((a, b) => a.sortOrder - b.sortOrder);
  });

  /**
   * Clicking (or keyboard-activating) a card row starts a new conversation with
   * the skill (SB-3325 PR-6), for owned and shared skills alike. Gated on
   * `ready` — mirrors the availability of the old start-new-conversation icon,
   * which was disabled while processing and hidden when failed.
   */
  public handleClickRowCTA(): void {
    if (this.status() !== 'ready') {
      return;
    }

    this.newConvoClicked.emit();
  }
}
